import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { DateTime } from 'luxon'
import { Box, Typography } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import { useSessionUID } from 'src/session/useSession'
import FailureMessage from '../../components/FailureMessage'
import { unreadBoundaryKey } from '../../api/patientConversation'
import { advancedColors, surfaces } from '../../theme'
import { useCareTeamChat } from './useCareTeamChat'
import { useCareTeamRoster } from './useCareTeamRoster'
import { useMarkCareTeamChatRead } from './useMarkCareTeamChatRead'
import { memberLabel } from './roleLine'
import { ChatBubbleEntry, ForwardedFeedItem, SendMessageError } from './types'
import { PendingAttachment } from './attachments/AttachmentPicker'
import { useAttachmentDownloadUrls } from './attachments/useAttachmentDownloadUrls'
import ChatThreadShell from './ChatThreadShell'

type Props = {
  uid: string
  // A null `threadId` is the group thread.
  threadId?: string | null
  showHeader?: boolean
  pendingForward?: ForwardedFeedItem | null
  onClearPendingForward?: () => void
  onMessageSent?: () => void
  // Called once the service has recorded the newest message as read.
  onRead?: () => void
}

const noop = () => undefined

// As the Feed does: the divider stays a moment after the thread is marked read, so the coach sees where it started.
const DIVIDER_LINGER_MS = 1000

const CareTeamChatThread = ({
  uid,
  threadId = null,
  showHeader = true,
  pendingForward,
  onClearPendingForward,
  onMessageSent,
  onRead = noop,
}: Props) => {
  const coachUid = useSessionUID()
  const { addToast } = useToasts()
  const { messages, readThrough, refresh, loading, error, sending, sendMessage } = useCareTeamChat(uid, threadId)
  const lingerTimer = useRef<number>()
  const handleRead = useCallback(() => {
    onRead()
    window.clearTimeout(lingerTimer.current)
    lingerTimer.current = window.setTimeout(refresh, DIVIDER_LINGER_MS)
  }, [onRead, refresh])
  useEffect(() => () => window.clearTimeout(lingerTimer.current), [])
  useMarkCareTeamChatRead(uid, threadId, messages, handleRead)
  const { roster, roleFor, categoryFor, nameFor } = useCareTeamRoster(uid, true)
  const memberNames = useMemo(() => new Map(roster.map((member) => [member.id, member.name])), [roster])

  const attachmentFileIDs = useMemo(
    () => messages.flatMap((message) => message.attachments.map((attachment) => attachment.fileID)),
    [messages]
  )
  const downloadUrls = useAttachmentDownloadUrls(attachmentFileIDs)

  const entries: ChatBubbleEntry[] = useMemo(
    () =>
      messages.map((message) => ({
        id: message.id,
        text: message.text,
        createdAt: message.createdAt,
        isMine: message.authorCoachId === coachUid,
        authorCoachId: message.authorCoachId,
        // Resolve historical senders too; never fall through to CoachName's abbreviated raw UID.
        authorName: memberNames.get(message.authorCoachId) ?? nameFor(message.authorCoachId),
        authorRole: roleFor(message.authorCoachId),
        authorCategory: categoryFor(message.authorCoachId) ?? 'system',
        forwardedItem: message.forwardedItem,
        attachments: message.attachments.map((attachment) => ({
          ...attachment,
          downloadURL: downloadUrls[attachment.fileID] ?? null,
        })),
      })),
    [messages, coachUid, memberNames, roleFor, categoryFor, nameFor, downloadUrls]
  )

  const unread = useMemo(() => {
    const readThroughMillis = readThrough ? DateTime.fromISO(readThrough).toMillis() : null
    const first = entries.find(
      (entry) =>
        entry.variant !== 'activity' &&
        !entry.isMine &&
        (readThroughMillis === null || DateTime.fromISO(entry.createdAt).toMillis() > readThroughMillis)
    )
    return { firstId: first?.id ?? null, boundaryKey: unreadBoundaryKey(readThrough) }
  }, [entries, readThrough])

  const send = async (text: string, attachments: PendingAttachment[], forwardedItem: ForwardedFeedItem | null) => {
    try {
      await sendMessage(text, attachments, forwardedItem)
    } catch (failure) {
      const { failure: described, message } = failure as SendMessageError
      addToast(described ? <FailureMessage failure={described} /> : message, { appearance: 'error' })
      throw failure
    }
    onMessageSent?.()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {showHeader && (
        <Box sx={{ px: 1.5, py: 1, backgroundColor: surfaces.blue.fg, color: advancedColors.surface }}>
          <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Full care team
          </Typography>
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {roster.length === 0 ? 'No care team assigned yet.' : roster.map(memberLabel).join(' · ')}
          </Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ChatThreadShell
          uid={uid}
          careTeam
          entries={entries}
          loading={loading}
          onSend={send}
          sending={sending}
          error={error}
          emptyLabel="No messages in the care-team chat yet."
          placeholder="Message the care team…"
          pendingForward={pendingForward}
          onClearPendingForward={onClearPendingForward}
          unread={unread}
        />
      </Box>
    </Box>
  )
}

export default CareTeamChatThread
